import type { ReactNode } from 'react';
import type { WorkOrderDto } from '@signalvote/shared';
import { WORK_ORDER_MAX_MESSAGE_CHARS, assembleTextSendMessage } from '@signalvote/shared';
import { Button, Card, CardContent, CardHeader, CardTitle, PendingButton } from '@/components/ui';
import { Label } from '@/components/ui/label';
import { getCampaignTodayString } from '@/lib/calendarUtils';
import { PhonePreview, PhonePreviewButton, TextComposer, textSendProblem } from './TextComposer';

/** The editable draft fields the container owns and persists. */
export type WorkOrderBuildField = 'message' | 'paidForDisclaimer' | 'sendDate' | 'sendTime';

interface WorkOrderBuildViewProps {
  workOrder: WorkOrderDto;
  /**
   * The controlled message / send date / send time. State lives in the screen container so it
   * can persist to the draft (PATCH) — this view stays presentational (ADR-068 build seam).
   */
  message?: string;
  paidForDisclaimer?: string;
  sendDate?: string;
  sendTime?: string;
  /** Called with the field key + new value as the operator edits a controlled field. */
  onFieldChange?: (field: WorkOrderBuildField, value: string) => void;
  /** Called when a controlled field loses focus — the container's on-blur save trigger. */
  onFieldBlur?: () => void;
  /**
   * A campaign-local-past send date/time error (the client mirror of the server's guard),
   * shown under the schedule fields and used to disable Submit & Pay.
   */
  scheduleError?: string | null;
  /** A human-readable error from a rejected persist (the server's 400), surfaced by the fields. */
  saveError?: string | null;
  /** Called with the picked file when the operator attaches/replaces the image. */
  onAttachImage?: (file: File) => void;
  /** True while an image upload is in flight (disables the picker, shows a hint). */
  isUploadingImage?: boolean;
  /** A human-readable upload error to surface under the picker, if the last attempt failed. */
  imageUploadError?: string | null;
  /**
   * Called when the admin clicks "Save draft" — persist the current fields and leave, without
   * invoicing or paying. The draft also auto-saves on blur; this is the explicit "I'm done
   * setting it up, someone can pay it later" action.
   */
  onSaveDraft?: () => void;
  /** Called when the admin clicks "Submit & Pay". The container checks the draft out. */
  onSubmitAndPay?: () => void;
  /** What checkout would bill now (the server's preview), or null while it isn't known. */
  priceCents?: number | null;
  /** True while the checkout call is in flight (disables and spins the pay button). */
  isSubmitting?: boolean;
  /** A human-readable submit error to surface under the pay button, if invoicing failed. */
  submitError?: string | null;
  /**
   * True when the campaign is not yet cleared to text (its messaging profile has not reached
   * Provisioned). The send is still paid now and waits for registration after (ADR-095), so this
   * only shows a note.
   */
  waitsForRegistration?: boolean;
  /** Why Submit & Pay is held under the smallest charge Stripe takes, or null when it isn't. */
  minimumNote?: string | null;
  /** False while the picked recipients have no list saved, which holds Submit & Pay. */
  recipientsSettled?: boolean;
  /** Who gets the text: the recipient picker, which the container wires. */
  recipients?: ReactNode;
}

/**
 * The draft build view for a text-send Work Order: who gets it (the container's recipient picker),
 * the shared composer with its preview, and the schedule. The composer counts the whole text as
 * voters get it (message, Paid-for disclaimer, Opt-out line) against the 2,000-character cap. The
 * send-date picker anchors "today" to the campaign timezone. Every field is controlled: its state
 * and the persist-on-blur PATCH live in the screen container.
 */
export function WorkOrderBuildView({
  workOrder,
  message = '',
  paidForDisclaimer = '',
  sendDate = '',
  sendTime = '',
  onFieldChange,
  onFieldBlur,
  scheduleError = null,
  saveError = null,
  onAttachImage,
  isUploadingImage = false,
  imageUploadError = null,
  onSaveDraft,
  onSubmitAndPay,
  priceCents = null,
  isSubmitting = false,
  submitError = null,
  waitsForRegistration = false,
  minimumNote = null,
  recipientsSettled = true,
  recipients,
}: WorkOrderBuildViewProps) {
  // Over the limit or without a disclaimer the vendor would refuse it, so it is never saved or billed.
  const textProblem = textSendProblem({ body: message, paidForDisclaimer });
  const overLimit = assembleTextSendMessage(message, paidForDisclaimer).length > WORK_ORDER_MAX_MESSAGE_CHARS;

  // "Today" anchored to the campaign timezone — the schedule floor, never browser-local.
  const campaignToday = getCampaignTodayString();

  const previewUrl = workOrder.details.imagePreviewUrl;
  const content = { body: message, paidForDisclaimer, imageUrl: previewUrl };

  // The money step is blocked while the message is over the cap (we never bill for a message
  // the vendor would reject) or the schedule is in the past.
  const submitBlocked = !!textProblem || !!scheduleError || !!minimumNote || !recipientsSettled;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Text send</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {waitsForRegistration && (
          <p data-testid="registration-wait-note" role="status" className="text-sm text-muted-foreground">
            Your campaign’s texting registration isn’t approved yet. You can pay now; this send waits for your
            registration and goes out once it’s approved.
          </p>
        )}

        {recipients}

        {priceCents !== null && (
          <p data-testid="price-note" className="text-sm text-muted-foreground">
            You’ll be billed <span className="font-semibold text-foreground">{formatPrice(priceCents)}</span> for this
            text send.
          </p>
        )}

        <div className="grid gap-8 md:grid-cols-[1fr_260px]">
          <div className="min-w-0 space-y-4">
            <TextComposer
              body={message}
              paidForDisclaimer={paidForDisclaimer}
              imageUrl={previewUrl}
              onBodyChange={(value) => onFieldChange?.('message', value)}
              onDisclaimerChange={(value) => onFieldChange?.('paidForDisclaimer', value)}
              onBlur={() => onFieldBlur?.()}
              onImagePick={onAttachImage}
              imageBusy={isUploadingImage}
              imageError={imageUploadError}
            />
            <PhonePreviewButton {...content} />
          </div>
          <div className="hidden md:block">
            <PhonePreview {...content} size="compact" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1">
            <Label htmlFor="work-order-send-date">Send date</Label>
            <input
              id="work-order-send-date"
              type="date"
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              value={sendDate}
              min={campaignToday}
              onChange={(e) => onFieldChange?.('sendDate', e.target.value)}
              onBlur={() => onFieldBlur?.()}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="work-order-send-time">Send time</Label>
            <input
              id="work-order-send-time"
              type="time"
              className="w-full rounded-md border border-input bg-background p-2 text-sm"
              value={sendTime}
              onChange={(e) => onFieldChange?.('sendTime', e.target.value)}
              onBlur={() => onFieldBlur?.()}
            />
          </div>
        </div>

        {/* Campaign-local-past schedule error (client mirror of the server's guard). */}
        {scheduleError && (
          <p data-testid="schedule-error" role="alert" className="text-sm text-destructive">
            {scheduleError}
          </p>
        )}

        {/* A rejected persist (the server's 400) — the edit did not save. */}
        {saveError && (
          <p data-testid="save-error" role="alert" className="text-sm text-destructive">
            {saveError}
          </p>
        )}

        {/*
          Submit & Pay: the container raises the draft's invoice, and its card form takes over.
          Blocked while a submit is in flight, the message is over the cap, or the schedule is in the past.
        */}
        <div className="space-y-2 pt-2">
          <div className="flex gap-2">
            {/* Save draft: persist and leave without paying (the draft also auto-saves on blur). */}
            <Button
              type="button"
              variant="outline"
              className="flex-1"
              disabled={!onSaveDraft || overLimit || !!scheduleError}
              onClick={() => onSaveDraft?.()}
            >
              Save draft
            </Button>
            <PendingButton
              type="button"
              className="flex-1"
              pending={isSubmitting}
              disabled={!onSubmitAndPay || submitBlocked}
              onClick={() => onSubmitAndPay?.()}
            >
              Submit &amp; Pay
            </PendingButton>
          </div>
          {minimumNote && (
            <p data-testid="minimum-note" role="status" className="text-sm text-muted-foreground">
              {minimumNote}
            </p>
          )}
          {submitError && (
            <p role="alert" className="text-sm text-destructive">
              {submitError}
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

const formatPrice = (cents: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(cents / 100);
